// CopyFormulaButton.jsx — "Copy visibility formula".
//
// FMX's API cannot set a visibility formula (the POST/PUT write model has no
// such property — see visibilityFormula.js), so the formula this tool derives
// has to be carried into FMX by hand. This button is that bridge, and it
// appears in three places: on the field card in the builder, in the Formulas
// bucket tab, and on the dry run (where the whole set can be copied at once,
// so the user can push and then work down the list in FMX).
//
// navigator.clipboard needs a secure context; the textarea + execCommand path
// keeps it working on plain-HTTP local dev.

import { useEffect, useRef, useState } from 'react';
import { T } from '../../theme';

export async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default function CopyFormulaButton({
  text, label = 'Copy visibility formula', className = 'fmx-btn-xs', style = {}, disabled = false,
}) {
  const [state, setState] = useState('idle'); // 'idle' | 'copied' | 'failed'
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onClick = async () => {
    const ok = await copyText(text);
    setState(ok ? 'copied' : 'failed');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 2000);
  };

  return (
    <button
      className={className}
      onClick={onClick}
      disabled={disabled || !text}
      title={text ? `Copy: ${text}` : 'No formula to copy'}
      style={{
        color: state === 'copied' ? T.greenText : state === 'failed' ? T.red500 : undefined,
        ...style,
      }}
    >
      <i className={`fa-solid fa-${state === 'copied' ? 'check' : state === 'failed' ? 'triangle-exclamation' : 'copy'}`} />{' '}
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Press ⌘/Ctrl+C' : label}
    </button>
  );
}
